<template>
  <CardSection :loading="isSavableStateLoading" :error="majorError">
    <template #header>
      Edit Mask CSV Zip
    </template>
    <template #body>
      <div v-if="maskCSVZip">
        <div class="row mb-3">
          <div class="col-12 d-flex align-items-center gap-2">
            <span class="status-badge" :class="maskCSVZip.statusBadgeClass">
              {{ maskCSVZip.statusDisplayText }}
            </span>
            <span v-if="maskCSVZip.details.stored_zip_file?.stored_file" class="text-muted small">
              {{ maskCSVZip.details.stored_zip_file.stored_file.original_filename }}
            </span>
          </div>
        </div>

        <div v-if="fileMaskingConfigs.length === 0" class="alert alert-info">
          No column information extracted yet.
        </div>

        <Accordion v-else :value="openPanels" multiple>
          <AccordionPanel
            v-for="(config, index) in fileMaskingConfigs"
            :key="config.filename"
            :value="String(index)"
          >
            <AccordionHeader>
              {{ config.filename }}
            </AccordionHeader>
            <AccordionContent>
              <div v-if="config.errors && config.errors.length > 0" class="alert alert-danger mb-3">
                <div v-for="err in config.errors" :key="err">{{ err }}</div>
              </div>
              <div class="row g-3">
                <div class="col-3" v-for="(_, key) in config.column_masks" :key="key">
                  <SelectInput
                    v-model="config.column_masks[key as string]"
                    :name="`${config.filename}_${String(key)}`"
                    :label="String(key)"
                    :options="maskingOptions"
                    label-key="label"
                    value-key="value"
                    col-style=""
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionPanel>
        </Accordion>

        <div class="mt-3">
          <ActionToolbar
            :component-state="currentState"
            :success-message="successMessage"
            :error-message="formError || 'An error occurred'"
            :primary-enabled="hasMaskingConfig"
            :primary-disabled="isProcessing"
            primary-button-text="Save Masking Config"
            primary-style="primary"
            primary-type="button"
            :secondary-enabled="hasMaskingConfig"
            :secondary-disabled="isProcessing"
            secondary-button-text="Run Masking"
            secondary-style="success"
            secondary-type="button"
            :tertiary-enabled="true"
            @primary-click="onSave"
            @secondary-click="onRunMasking"
          >
            <template #tertiary-button-area>
              <button
                v-if="hasMaskedZip"
                type="button"
                class="btn btn-success mr-2"
                :disabled="isSavableStateProcessing || isProcessing"
                @click="onDownload"
              >
                Download Masked Zip
              </button>
              <router-link :to="{ name: 'admin_unos_mask_csv' }" class="btn btn-outline-secondary">
                Back
              </router-link>
            </template>
          </ActionToolbar>
        </div>
      </div>
    </template>
  </CardSection>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';
import CardSection from "@/src/components/shared/CardSection.vue";
import ActionToolbar from "@/src/components/shared/ActionToolbar.vue";
// @ts-ignore
import SelectInput from "@/src/components/shared/FormComponents/SelectInput.vue";
import { useSavableState } from "@/src/composables/useSavableState";
import { AvailableMaskings, MaskCSVZip } from "@/src/models/unos/mask-csv-zip";
import beApiClient from "@/src/be-api-client";

const {
  isSavableStateLoading,
  isSavableStateProcessing,
  currentState,
  majorError,
  formError,
  performAsyncLoading,
  setSavableStateProcessing,
  setSavableStateSuccess,
  setSavableStateIdle,
  handleFormError,
} = useSavableState();

const route = useRoute();

const maskCSVZip = ref<MaskCSVZip | null>(null);
const successMessage = ref('');
const availableMaskings = ref<AvailableMaskings>({});

const humanizeMaskingKey = (key: string): string =>
  key.split('_').map(word => word.charAt(0) + word.slice(1).toLowerCase()).join(' ');

const maskingOptions = computed(() => [
  // Blank label so both "never set" and "explicitly cleared" render the same way in the select.
  { label: '', value: null as string | null },
  ...Object.entries(availableMaskings.value).map(([key, value]) => ({
    label: humanizeMaskingKey(key),
    value,
  })),
]);

const maskCSVZipID = computed<string | null>(() =>
  route.params.id ? route.params.id.toString() : null
);

const fileMaskingConfigs = computed(() =>
  maskCSVZip.value?.masking_configuration?.file_masking_config ?? []
);

const openPanels = ref<string[]>([]);

// Save/Run are usable whenever we have a masking config to work with (i.e., columns extracted).
const hasMaskingConfig = computed(() => fileMaskingConfigs.value.length > 0);
// Download is usable once a masked zip has actually been produced.
const hasMaskedZip = computed(() => !!maskCSVZip.value?.details?.masked_zip_file?.stored_file);
// Server-side processing states. If a user refreshes mid-masking, local savable-state
// resets to Blank, so we also gate buttons on the persisted status so they can't kick
// off a second masking run while one is already in flight.
const isProcessing = computed(() =>
  ['processing_columns', 'masking_files'].includes(maskCSVZip.value?.status ?? '')
);

// When masking finishes with errors, auto-expand every accordion panel whose file has errors
// so the user can see them without hunting.
watch(() => maskCSVZip.value?.status, (status) => {
  if (status !== 'masking_complete_with_errors') return;

  const errorIndices = fileMaskingConfigs.value
    .map((config, index) => (config.errors?.length ? String(index) : null))
    .filter((v): v is string => v !== null);

  openPanels.value = Array.from(new Set([...openPanels.value, ...errorIndices]));
});

// Masking runs in a Sidekiq worker (see CreateMaskedZipWorker). While the record is in a
// processing state, poll the show endpoint so the UI reflects completion without a manual
// refresh. Vue's watchEffect handles both re-run and unmount cleanup for us.
const POLL_INTERVAL_MS = 5000;
watchEffect((onCleanup) => {
  if (!isProcessing.value || !maskCSVZipID.value) return;

  const handle = setInterval(async () => {
    try {
      maskCSVZip.value = await MaskCSVZip.loadByID(maskCSVZipID.value!);
    } catch {
      // Swallow transient network errors; the next tick will retry.
    }
  }, POLL_INTERVAL_MS);

  onCleanup(() => clearInterval(handle));
});

const loadMaskCSVZip = async () => {
  await performAsyncLoading(async () => {
    if (!maskCSVZipID.value) return;
    maskCSVZip.value = await MaskCSVZip.loadByID(maskCSVZipID.value);
  });
};

const onSave = async () => {
  if (!maskCSVZip.value || !maskCSVZipID.value) return;
  try {
    setSavableStateProcessing();
    maskCSVZip.value = await MaskCSVZip.update(
      maskCSVZipID.value,
      maskCSVZip.value.masking_configuration
    );
    successMessage.value = 'Masking configuration saved.';
    setSavableStateSuccess();
  } catch (error: any) {
    handleFormError(error?.response?.data?.errors || error.message);
  }
};

const onRunMasking = async () => {
  if (!maskCSVZipID.value) return;
  try {
    setSavableStateProcessing();
    maskCSVZip.value = await MaskCSVZip.runMasking(maskCSVZipID.value);
    setSavableStateIdle();
  } catch (error: any) {
    handleFormError(error?.response?.data?.errors || error.message);
  }
};

const onDownload = async () => {
  if (!maskCSVZipID.value || !maskCSVZip.value) return;
  try {
    const url = MaskCSVZip.downloadMaskedZipUrl(maskCSVZipID.value);
    const response = await beApiClient.get(url, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'application/zip' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = maskCSVZip.value.details.masked_zip_file?.stored_file?.original_filename ?? 'masked.zip';
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error: any) {
    handleFormError(error?.response?.data?.errors || error.message);
  }
};

onMounted(async () => {
  availableMaskings.value = await MaskCSVZip.loadAvailableMaskings();
  await loadMaskCSVZip();
});
</script>

<style scoped>
.status-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.2;
  border-radius: 0.25rem;
  text-align: center;
  white-space: nowrap;
  text-transform: uppercase;
}
</style>
